import classNames from 'classnames';
import { DateRange } from 'components/atoms/DatePicker/DatePicker.component';
import { Checkbox } from 'components/atoms/Form/Checkbox';
import RadioGroups from 'components/atoms/RadioGroups/RadioGroups.component';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterV2 } from 'types/global.types';
import type { FiltersConfigV2 } from 'types/shared/filters';
import SearchBar from './SearchBar';

type FilterCellV2Props = {
  filter: FilterV2 | null;
  value: any;
  onChange: FiltersConfigV2['onChange'];
  menuPosition?: 'bottom-start' | 'bottom-end';
};

type OptionsV2Props = Omit<FilterCellV2Props, 'menuPosition'>;

type Option = {
  label: string;
  value: string;
};

export const OptionsV2: FC<OptionsV2Props> = ({
  filter,
  value: filterValue,
  onChange,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  if (filter?.type === 'radio') {
    return (
      <>
        <SearchBar
          placeholder={t('search') + '...'}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          className="w-full py-2 px-4"
        />

        <div className="px-4 pb-4 flex-1 w-full">
          <RadioGroups
            type="block"
            onChange={(v) => onChange(filter.key, v)}
            labelClassNames="!font-semibold !text-sm text-primary truncate-wrap"
            data={filter.options
              .filter((option: Option) =>
                option.label.toLowerCase().includes(searchValue),
              )
              .map((option: Option) => ({
                id: option.value,
                title: option.label,
                checked: option.value === filterValue,
              }))}
          />
        </div>
      </>
    );
  }

  if (filter?.type === 'checkbox') {
    const _filterValue: string[] = filterValue || [];

    return (
      <>
        <SearchBar
          placeholder={t('search') + '...'}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          className="w-full py-2 px-4"
        />

        <div className="px-4 pb-4 flex-1 overflow-y-auto space-y-2 force-scroll-style w-full">
          {filter.options
            .filter((option: Option) =>
              option.label.toLowerCase().includes(searchValue),
            )
            .map(({ value, label }: Option) => {
              const checked = _filterValue.includes(value);
              return (
                <div key={value} className="flex items-center py-1">
                  <Checkbox
                    id={value}
                    onChange={() =>
                      onChange(
                        filter.key,
                        checked
                          ? _filterValue.filter((s) => s !== value)
                          : _filterValue.concat(value),
                      )
                    }
                    checked={checked}
                  />
                  <label
                    htmlFor={value}
                    className={classNames(
                      'font-semibold text-sm text-primary truncate-wrap',
                      'pl-3 flex-1 block cursor-pointer',
                    )}
                  >
                    {label}
                  </label>
                </div>
              );
            })}
        </div>
      </>
    );
  }

  if (filter?.type === 'date-range') {
    return (
      <>
        <div className="py-2 px-4 flex-1 w-full">
          <DateRange
            start={filter.options[0]}
            end={filter.options[1]}
            value={filterValue}
            customInputprops={{ size: 'sm' }}
            onChange={(start, end) => {
              if (!start || !end) return onChange(filter.key, undefined);
              onChange(filter.key, [start, end]);
            }}
          />
        </div>
      </>
    );
  }

  return null;
};
